/**
 * TerrainBatch — every chunk of a layer, at EVERY lod, drawn in ONE multi-draw call.
 *
 * Replaces the ChunkGrouper's merged 3×3×3 groups (docs/terrain-batching-plan.md). A THREE.BatchedMesh
 * per layer × kind owns one big vertex/index buffer; each chunk holds a reserved range in it (a
 * `geometryId`) and one instance (a matrix — the chunk's world offset — and a visibility bit). The
 * lod rides on the vertices (`lodLevel`, lodTexScale.ts) and the scale is baked into positions, so
 * nothing per draw call depends on it: six batches draw the whole terrain. What that buys, against
 * the grouper:
 *
 *  - A remesh is a copy of THAT CHUNK into its range (`setGeometryAt`), cost ∝ its own vertices —
 *    not a re-bake of 27 neighbours. There is no merge, so there is no merge budget, no vertex
 *    admission, no starvation escape, no "standalone mesh while the merge is deferred".
 *  - A display flip is `setVisibleAt`: immediate, always. A chunk has ONE display state instead of
 *    three that had to agree (standalone / merged slice / suppressed), which is where the
 *    display-contract regressions came from. C4's "applied means on screen" holds by construction.
 *  - Seam normal writes, band masks and light resamples re-copy one chunk (`refreshChunk`) or one
 *    attribute range (`updateChunkLight`) — one target, not a per-chunk copy AND a merged copy.
 *  - Aprons and skirts are ordinary geometries in their own batches; their flip-lifetime is fine.
 *  - Draw calls: one per live (layer, kind) batch — at most six.
 *
 * WHAT THE BATCH DOES NOT DO: it does not move the GPU number. Shaders that compute world position
 * from `modelMatrix * position` must apply `batchingMatrix` first (terrainShaders.ts does), or the
 * tri-planar mapping samples the chunk-local position.
 *
 * RESERVATIONS. A chunk's vertex count changes on every remesh, so its range is reserved with slack
 * (RESERVE_FACTOR); a remesh that fits writes in place. One that does not — and every eviction —
 * RELEASES its range to a per-batch FREE LIST, and a later chunk that fits a freed range takes it
 * over (`setGeometryAt` into the kept geometryId): ranges are RECYCLED, not compacted. Free ranges
 * cost nothing — the buffer is allocated at capacity either way — so dead space only matters when it
 * would force a growth, and that is the one time `optimize()` still runs (ensureRoom compacts before
 * it grows). Growth itself is pre-emptive: `tick()` grows a batch crossing 15/16 occupancy on a frame
 * of its choosing, instead of the apply that tips it over (a growth is a full re-upload; logged).
 *
 * Positions are still Float32 chunk-local with a translate matrix per instance; the quantised layout
 * (plan §3) drops in here later — the instance matrix is already the dequantise transform.
 */
import * as THREE from 'three';
import { CHUNK_WORLD_SIZE, VISIBILITY_RADIUS, VOXEL_SCALE, packChunkKeyLod, parseChunkKey } from '@worldify/shared';
import {
  LAYER_COUNT, LAYER_LIQUID, SKIRT_KIND_ATTR, bufferAttribute, configureLayerMesh, layerMaterial,
} from './LayerConfig.js';
import { setLodLevelAttr } from './lodTexScale.js';
import { SKIRT_FACE, type ApronMesh } from './SeamStitcher.js';
import { TERRAIN_ATTRS } from './vertexFormat.js';

// ---- Sizing ----

/**
 * First allocation, vertices, by what the batch will hold: the lod-0 solid layer is most of the
 * world (measured ~600k live vertices at view distance VISIBILITY_RADIUS, cold), the other layers and
 * the rings are a fraction. Every growth is a full re-upload of the batch, so start near the steady
 * state — a cold load measured 8 growths from a flat 128k. The lod-0 share SCALES WITH THE VIEW
 * DISTANCE (resident lod-0 chunks grow with the radius squared — `viewScale`, see setViewDistance);
 * the rings' share is a constant (each ring is a roughly fixed chunk count; ~128k per ring measured,
 * four rings' worth here). Rounded up to whole 64k so the index buffer is Uint32 from birth
 * (BatchedMesh picks the index type from the vertex capacity at initialisation).
 */
function initialVerts(layer: number, kind: Kind, viewScale: number): number {
  if (kind === 'apron') return 1 << 18;
  const base = layer === 0 ? 1 << 20 : layer === 1 ? 1 << 18 : 1 << 17;
  const rings = layer === 0 ? 1 << 19 : 1 << 18;
  return Math.ceil((base * viewScale + rings) / (1 << 16)) * (1 << 16);
}
/** Reservation over a chunk's current count: slack so most remeshes write in place, floored. */
function reserve(n: number, floor: number): number {
  return Math.max(floor, Math.ceil(n * RESERVE_FACTOR));
}

type TypedArray = Float32Array | Uint8Array | Uint16Array | Uint32Array | Int8Array | Int16Array | Int32Array;

/** BatchedMesh's per-geometry bookkeeping (three r183 `_geometryInfo`), the fields the copy touches. */
interface GeometryInfoLike {
  vertexStart: number; reservedVertexCount: number; vertexCount: number;
  indexStart: number; reservedIndexCount: number; indexCount: number;
  start: number; count: number;
  boundingBox: THREE.Box3 | null; boundingSphere: THREE.Sphere | null;
  active: boolean;
}

/**
 * `setGeometryAt`, without three's per-element loops. Three rebases every index through
 * `setX`/`getX`, zero-fills the reservation's slack one component at a time, and uploads the whole
 * reserved range; for a chunk that is more JS than the copy itself. Here each attribute is one
 * typed-array `set`, the index rebase is one tight loop, and only the USED prefix is uploaded — the
 * slack is never drawn (the draw range is `count`), so it needs no zeroing. Falls back to three's
 * own path whenever the internals do not look as expected.
 */
function copyGeometryAt(mesh: THREE.BatchedMesh, geometryId: number, geo: THREE.BufferGeometry): void {
  const internals = mesh as unknown as { _geometryInfo?: GeometryInfoLike[]; _visibilityChanged: boolean };
  const info = internals._geometryInfo?.[geometryId];
  const batchGeo = mesh.geometry;
  const srcIndex = geo.index;
  const dstIndex = batchGeo.index;
  const verts = geo.getAttribute('position').count;
  if (!info || !info.active || !srcIndex || !dstIndex
    || verts > info.reservedVertexCount || srcIndex.count > info.reservedIndexCount) {
    mesh.setGeometryAt(geometryId, geo);
    return;
  }
  for (const name in batchGeo.attributes) {
    const dst = batchGeo.getAttribute(name) as THREE.BufferAttribute;
    const src = geo.getAttribute(name) as THREE.BufferAttribute | undefined;
    if (!src || src.itemSize !== dst.itemSize || src.array.constructor !== dst.array.constructor) {
      mesh.setGeometryAt(geometryId, geo);
      return;
    }
  }
  const vs = info.vertexStart;
  for (const name in batchGeo.attributes) {
    const dst = batchGeo.getAttribute(name) as THREE.BufferAttribute;
    const src = geo.getAttribute(name) as THREE.BufferAttribute;
    const n = verts * dst.itemSize;
    (dst.array as TypedArray).set((src.array as TypedArray).subarray(0, n) as never, vs * dst.itemSize);
    dst.addUpdateRange(vs * dst.itemSize, n);
    dst.needsUpdate = true;
  }
  const idx = srcIndex.count;
  const is = info.indexStart;
  const da = dstIndex.array as Uint32Array | Uint16Array;
  const sa = srcIndex.array as Uint32Array | Uint16Array;
  for (let i = 0; i < idx; i++) da[is + i] = vs + sa[i];
  dstIndex.addUpdateRange(is, idx);
  dstIndex.needsUpdate = true;
  info.vertexCount = verts;
  info.indexCount = idx;
  info.start = is;
  info.count = idx;
  info.boundingBox = geo.boundingBox ? geo.boundingBox.clone() : null;
  info.boundingSphere = geo.boundingSphere ? geo.boundingSphere.clone() : null;
  internals._visibilityChanged = true;
}

// ---- High-water memory ----
//
// Demand is BIOME-DEPENDENT and no fixed pre-size wins every world: a building world quadruples the
// solid layer, a tree world grew the transparent layer 16× (2M verts against a 131k initial), and a
// batch is recreated on every world/mode switch — which re-paid the same growth ladder, one logged
// hitch per rung, every load. So the ladder is paid ONCE: every growth records the capacity it
// reached, per batch id, and a new batch starts at its high-water mark. Kept at module level so it
// survives dispose(), and mirrored to localStorage so it survives the session (per machine — which
// is what a capacity is about). Clamped: a corrupt or stale entry must never allocate absurdly.
const HW_KEY = 'worldify.terrainBatch.highWater.v2';
const HW_MAX_VERTS = 1 << 22;
const HW_MAX_IDX = HW_MAX_VERTS * 6; // × INDICES_PER_VERT (declared below — hoisting keeps them apart)
const highWater = new Map<string, { verts: number; idx: number }>();
let highWaterLoaded = false;

function loadHighWater(): void {
  if (highWaterLoaded) return;
  highWaterLoaded = true;
  try {
    const raw = globalThis.localStorage?.getItem(HW_KEY);
    if (!raw) return;
    const obj = JSON.parse(raw) as Record<string, { verts: number; idx: number }>;
    for (const [id, hw] of Object.entries(obj)) {
      if (typeof hw?.verts === 'number' && typeof hw?.idx === 'number') {
        highWater.set(id, {
          verts: Math.min(HW_MAX_VERTS, Math.max(0, hw.verts)),
          idx: Math.min(HW_MAX_IDX, Math.max(0, hw.idx)),
        });
      }
    }
  } catch { /* storage unavailable (headless, blocked) — in-memory still works */ }
}

function noteHighWater(id: string, verts: number, idx: number): void {
  const prev = highWater.get(id);
  if (prev && prev.verts >= verts && prev.idx >= idx) return;
  highWater.set(id, {
    verts: Math.min(HW_MAX_VERTS, Math.max(prev?.verts ?? 0, verts)),
    idx: Math.min(HW_MAX_IDX, Math.max(prev?.idx ?? 0, idx)),
  });
  try {
    globalThis.localStorage?.setItem(HW_KEY, JSON.stringify(Object.fromEntries(highWater)));
  } catch { /* best effort */ }
}
/**
 * Index capacity per vertex of capacity. SurfaceNets emits ~one vertex per surface cell and ~one quad
 * per crossing lattice edge → triangles ≈ 2 × vertices, indices = 3 × triangles ≈ 6 × vertices (each
 * vertex shared by ~6 triangles, valence-6 grid). Measured in-app: a settled lod-0 solid batch ran at
 * exactly 6.0. The original 3 exhausted every index buffer at ~50% vertex occupancy — most of the
 * observed growths were index-only doublings.
 */
const INDICES_PER_VERT = 6;
const INITIAL_INSTANCES = 4096;
/** Slack reserved over a chunk's current vertex/index count, so most remeshes write in place. */
const RESERVE_FACTOR = 1.25;
const RESERVE_FLOOR_VERTS = 64;
const RESERVE_FLOOR_INDICES = 192;
/** A free range may be taken by a chunk down to this share of it while the tail also fits — a tiny
 *  chunk parked in a huge range is waste the tail would not have cost. Past-tail pressure takes any fit. */
const REUSE_MAX_FACTOR = 2;
/** Pre-emptive growth: tick() grows a batch whose tail crosses this occupancy (vertex or index side),
 *  so the growth lands on a frame of tick's choosing, not on the apply that tips the batch over. */
const GROW_AT_NUM = 15, GROW_AT_DEN = 16;

// ---- Types ----

/** Terrain chunks and seam strips live in separate batches: strips carry `skirtKind`, terrain does not,
 *  and a BatchedMesh needs one attribute set. */
type Kind = 'terrain' | 'apron';

interface LayerBatch {
  mesh: THREE.BatchedMesh;
  layer: number;
  kind: Kind;
  vertCap: number;
  idxCap: number;
  instCap: number;
  /** Next free start — mirrors BatchedMesh's `_nextVertexStart` / `_nextIndexStart`. */
  tailVerts: number;
  tailIdx: number;
  /** Reserved by ACTIVE geometries; `tail − live` is the dead space a compaction reclaims. */
  liveVerts: number;
  liveIdx: number;
  instances: number;
  /** Released ranges awaiting reuse — geometryIds kept alive, instance-less (they draw nothing).
   *  `tail − live` equals their reservations. */
  free: FreeRange[];
  /** Room an apply asked for and was refused (hasRoomFor) — made by the next tick, not by the apply. */
  wantVerts: number;
  wantIdx: number;
  wantInst: number;
}

interface FreeRange {
  geometryId: number;
  reservedVerts: number;
  reservedIdx: number;
}

interface Placement {
  batch: LayerBatch;
  geometryId: number;
  instanceId: number;
  reservedVerts: number;
  reservedIdx: number;
  /** The geometry last copied in — `refreshChunk` re-copies it after an in-place edit. */
  source: THREE.BufferGeometry;
}

interface ChunkEntry {
  key: string;
  cx: number; cy: number; cz: number;
  lod: number;
  wx: number; wy: number; wz: number;
  /** The walk's verdict. What is on screen is `visible && !previewHidden`. */
  visible: boolean;
  layers: (Placement | null)[];
}

const EMPTY_KEYS: ReadonlySet<string> = new Set();
const _m4 = new THREE.Matrix4();
const _v3 = new THREE.Vector3();

export class TerrainBatch {
  /** TESTS ONLY: forget remembered capacities, so sizing assertions see the model, not history. */
  static resetHighWater(): void {
    highWater.clear();
    highWaterLoaded = true; // do not reload what was just cleared
    try { globalThis.localStorage?.removeItem(HW_KEY); } catch { /* best effort */ }
  }

  private readonly batches = new Map<string, LayerBatch>();
  private readonly entries = new Map<string, ChunkEntry>();
  /** Chunks whose pixels the build preview owns right now — hidden here whatever the walk says. */
  private readonly previewHidden = new Set<string>();
  /** Displayed chunks by packChunkKeyLod — `isVisible` for callers holding coordinates. */
  private readonly visiblePacked = new Set<number>();
  /** Seam strips, keyed `${owner}|${face}|${layer}` (face SKIRT_FACE for owner-level skirts). */
  private readonly aprons = new Map<string, Placement>();

  /** Counters for the panel / audit: compactions and growths this session, and applies that waited. */
  compactions = 0;
  growths = 0;
  roomWaits = 0;
  /** lod-0 capacity multiplier from the view distance — (radius / VISIBILITY_RADIUS)², see setViewDistance. */
  private viewScale = 1;
  /** Closed while the shader warm-up runs (render/ShaderWarmup.ts): no terrain draw before its program is linked. */
  private drawGate = true;

  constructor(private readonly scene: THREE.Scene) {}

  /**
   * Size the batches' lod-0 share for this view distance. Only batches created AFTER the call take the
   * new size (they are created lazily, and dispose() on a world switch recreates them); a batch that
   * already exists keeps its capacity and grows through tick() if the new radius needs more.
   */
  setViewDistance(radius: number): void {
    const r = Math.max(1, radius) / VISIBILITY_RADIUS;
    this.viewScale = Math.min(8, Math.max(0.25, r * r));
  }

  /** Open or close the terrain draw gate; applies to every batch, existing and future. */
  setDrawGate(open: boolean): void {
    this.drawGate = open;
    for (const b of this.batches.values()) b.mesh.visible = open;
  }

  /** Create every batch now, so their buffers upload in the warm-up render rather than on the first chunk. */
  ensureBatches(): void {
    for (let layer = 0; layer < LAYER_COUNT; layer++) {
      this.batchFor(layer, 'terrain');
      this.batchFor(layer, 'apron');
    }
  }

  // ============== Chunks ==============

  /**
   * Would updateChunk place these layer sizes WITHOUT compacting or growing a batch? The apply path
   * asks first: a compaction or a growth is a full-buffer rewrite (tens of ms on the lod-0 solid
   * batch), and inside an apply it was the `applies` peak — one apply, the one that tipped a batch
   * over, costing 41 ms. A refused apply stays queued; the batch records what it wanted and the next
   * tick() makes the room, so the same apply lands a frame later at its own cost.
   *
   * Mirrors place()'s decision exactly: a layer that still fits its reserved range needs no room; one
   * that does not needs a tail reservation and (unless it is re-placed within the same batch) an
   * instance slot.
   */
  hasRoomFor(key: string, lod: number, slots: ReadonlyArray<{ verts: number; idx: number } | null>): boolean {
    const entry = this.entries.get(key);
    const existing = entry && entry.lod === lod ? entry : undefined;
    let ok = true;
    for (let layer = 0; layer < LAYER_COUNT; layer++) {
      const slot = slots[layer];
      if (!slot || slot.idx === 0) continue;
      const b = this.batchFor(layer, 'terrain');
      const p = existing?.layers[layer];
      const same = p !== null && p !== undefined && p.batch === b;
      if (same && slot.verts <= p.reservedVerts && slot.idx <= p.reservedIdx) continue;
      const rv = reserve(slot.verts, RESERVE_FLOOR_VERTS);
      const ri = reserve(slot.idx, RESERVE_FLOOR_INDICES);
      const needInst = same ? 0 : 1;
      const fitsFree = b.free.some((fr) => fr.reservedVerts >= slot.verts && fr.reservedIdx >= slot.idx);
      const fitsTail = b.tailVerts + rv <= b.vertCap && b.tailIdx + ri <= b.idxCap;
      const fitsInst = b.instances + needInst <= b.instCap;
      if ((fitsTail || fitsFree) && fitsInst) continue;
      if (!fitsTail && !fitsFree) { b.wantVerts = Math.max(b.wantVerts, rv); b.wantIdx = Math.max(b.wantIdx, ri); }
      if (!fitsInst) b.wantInst = 1;
      ok = false;
    }
    if (!ok) this.roomWaits++;
    return ok;
  }

  /**
   * Register or replace a chunk's geometries (one per layer, null where the layer is empty).
   * Called after every mesh apply. A NEW chunk registers HIDDEN at every lod — it may only draw via
   * the LOD selection walk, which the apply re-triggers in the same frame (bug M2: lod 0 defaulting
   * visible flashed every freshly-applied mesh ahead of the walk's verdict).
   */
  updateChunk(
    key: string, cx: number, cy: number, cz: number,
    geometries: (THREE.BufferGeometry | null)[],
    worldPos: { x: number; y: number; z: number },
    lod: number = 0,
  ): void {
    let entry = this.entries.get(key);
    if (entry && entry.lod !== lod) { this.removeChunk(key); entry = undefined; }
    if (!entry) {
      entry = {
        key, cx, cy, cz, lod, wx: worldPos.x, wy: worldPos.y, wz: worldPos.z,
        visible: false, layers: new Array<Placement | null>(LAYER_COUNT).fill(null),
      };
      this.entries.set(key, entry);
    } else {
      entry.cx = cx; entry.cy = cy; entry.cz = cz;
      entry.wx = worldPos.x; entry.wy = worldPos.y; entry.wz = worldPos.z;
    }
    for (let layer = 0; layer < LAYER_COUNT; layer++) {
      this.placeChunkLayer(entry, layer, geometries[layer] ?? null);
    }
  }

  /** The walk's verdict for this chunk (what seam ownership follows). */
  isVisible(key: string): boolean {
    return this.entries.get(key)?.visible ?? false;
  }

  /** `isVisible` by packed key (packChunkKeyLod): the stitcher's resolve path asks this per lattice
   *  vertex and used to build a key string for each ask. Same idiom as VoxelWorld.chunkIndex. */
  isVisiblePacked(packed: number): boolean {
    return this.visiblePacked.has(packed);
  }

  isVisibleAt(cx: number, cy: number, cz: number, lod: number): boolean {
    return this.visiblePacked.has(packChunkKeyLod(cx, cy, cz, lod));
  }

  /**
   * Apply a display flip — on screen in this call (C4). Returns whether the state changed; false
   * also when the chunk has no registered geometry yet (the DisplayCommit's veto).
   */
  setVisible(key: string, visible: boolean): boolean {
    const entry = this.entries.get(key);
    if (!entry || entry.visible === visible) return false;
    entry.visible = visible;
    const packed = packChunkKeyLod(entry.cx, entry.cy, entry.cz, entry.lod);
    if (visible) this.visiblePacked.add(packed); else this.visiblePacked.delete(packed);
    this.applyVisibility(entry);
    return true;
  }

  /** Are this chunk's pixels on screen — shown by the walk, not taken by the preview, and drawing
   *  geometry in some layer? */
  isChunkDisplayed(key: string): boolean {
    const entry = this.entries.get(key);
    if (!entry || !entry.visible || this.previewHidden.has(key)) return false;
    const l = entry.layers;
    return l[0] !== null || l[1] !== null || l[2] !== null;
  }

  /** Evict a chunk: its ranges are freed and its pixels stop in this call. */
  removeChunk(key: string): void {
    const entry = this.entries.get(key);
    if (!entry) return;
    for (let layer = 0; layer < LAYER_COUNT; layer++) {
      const p = entry.layers[layer];
      if (p) { this.release(p); entry.layers[layer] = null; }
    }
    this.entries.delete(key);
    this.visiblePacked.delete(packChunkKeyLod(entry.cx, entry.cy, entry.cz, entry.lod));
    this.previewHidden.delete(key);
  }

  /**
   * The chunk's geometry was edited IN PLACE — reconciled seam normals, a band mask over its index
   * buffer, a light resample — and the batch holds a copy. Re-copy it: cost ∝ this chunk. Zero
   * frames of latency, no fallback path (the grouper's patchChunk* had to give up whenever the merged
   * slice could not be trusted; a range is always its own chunk's).
   */
  refreshChunk(key: string): void {
    const entry = this.entries.get(key);
    if (!entry) return;
    for (let layer = 0; layer < LAYER_COUNT; layer++) {
      const p = entry.layers[layer];
      if (p) this.placeChunkLayer(entry, layer, p.source);
    }
  }

  /**
   * The stitcher rewrote seam NORMALS in place on the layers `layerMask` names: write only that
   * attribute's range for those layers. The rest of the vertex and the whole index are untouched, so
   * copying them (refreshChunk) was the bulk of the flush on a dense chunk. Falls back to a full
   * re-copy of a layer whose vertex count no longer matches its range.
   */
  updateChunkNormals(key: string, layerMask: number): void {
    const entry = this.entries.get(key);
    if (!entry) return;
    for (let layer = 0; layer < LAYER_COUNT; layer++) {
      if ((layerMask & (1 << layer)) === 0) continue;
      const p = entry.layers[layer];
      if (!p) continue;
      const src = p.source.getAttribute('normal') as THREE.BufferAttribute | undefined;
      if (!src) continue;
      const range = p.batch.mesh.getGeometryRangeAt(p.geometryId);
      if (!range || range.vertexCount !== src.count) { this.placeChunkLayer(entry, layer, p.source); continue; }
      const dst = p.batch.mesh.geometry.getAttribute('normal') as THREE.BufferAttribute;
      const start = range.vertexStart * dst.itemSize;
      const n = src.count * dst.itemSize;
      (dst.array as TypedArray).set((src.array as TypedArray).subarray(0, n) as never, start);
      dst.addUpdateRange(start, n);
      dst.needsUpdate = true;
    }
  }

  /**
   * The stitcher rewrote a layer's INDEX buffer in place (a band mask collapsing triangles, or its
   * restore): rebase and write only the index range of the layers `layerMask` names. Vertices did
   * not move, so no attribute is copied. Falls back to a full re-copy on an index count mismatch.
   */
  updateChunkIndices(key: string, layerMask: number): void {
    const entry = this.entries.get(key);
    if (!entry) return;
    for (let layer = 0; layer < LAYER_COUNT; layer++) {
      if ((layerMask & (1 << layer)) === 0) continue;
      const p = entry.layers[layer];
      if (!p) continue;
      const srcIndex = p.source.index;
      if (!srcIndex) continue;
      const range = p.batch.mesh.getGeometryRangeAt(p.geometryId);
      if (!range || range.indexCount !== srcIndex.count) { this.placeChunkLayer(entry, layer, p.source); continue; }
      const dstIndex = p.batch.mesh.geometry.index!;
      const da = dstIndex.array as Uint32Array | Uint16Array;
      const sa = srcIndex.array as Uint32Array | Uint16Array;
      const vs = range.vertexStart, is = range.indexStart, count = srcIndex.count;
      for (let i = 0; i < count; i++) da[is + i] = vs + sa[i];
      dstIndex.addUpdateRange(is, count);
      dstIndex.needsUpdate = true;
    }
  }

  /** Light-only resample landed in the chunk's `lightLevel` / `blockLight` attributes: write just those
   *  two ranges. Falls back to a full re-copy if the vertex count no longer matches. */
  updateChunkLight(key: string): void {
    const entry = this.entries.get(key);
    if (!entry) return;
    for (let layer = 0; layer < LAYER_COUNT; layer++) {
      const p = entry.layers[layer];
      if (!p) continue;
      const src = p.source;
      const srcLight = src.getAttribute('lightLevel') as THREE.BufferAttribute | undefined;
      const srcBlock = src.getAttribute('blockLight') as THREE.BufferAttribute | undefined;
      if (!srcLight || !srcBlock) continue;
      const range = p.batch.mesh.getGeometryRangeAt(p.geometryId);
      if (!range || range.vertexCount !== srcLight.count) { this.placeChunkLayer(entry, layer, src); continue; }
      const geo = p.batch.mesh.geometry;
      const dstLight = geo.getAttribute('lightLevel') as THREE.BufferAttribute;
      const dstBlock = geo.getAttribute('blockLight') as THREE.BufferAttribute;
      // Normalized bytes on both sides (vertexFormat.ts); offsets in vertices (itemSize 1).
      (dstLight.array as Uint8Array).set(srcLight.array as Uint8Array, range.vertexStart);
      (dstBlock.array as Uint8Array).set(srcBlock.array as Uint8Array, range.vertexStart);
      dstLight.addUpdateRange(range.vertexStart, range.vertexCount);
      dstLight.needsUpdate = true;
      dstBlock.addUpdateRange(range.vertexStart, range.vertexCount);
      dstBlock.needsUpdate = true;
    }
  }

  // ============== Build preview ==============
  //
  // The grouper had to hide a whole 27-chunk group and raise standalones for the other 26 members;
  // here the preview simply takes the chunks it draws. Always immediate, so BuildPreview's deferred
  // path never engages (hasPendingSuppressions is always false).

  /** The preview now draws exactly these chunks; anything previously taken and not listed is given back. */
  suppressGroupsForChunks(previewChunkKeys: ReadonlySet<string>): boolean {
    for (const key of [...this.previewHidden]) {
      if (!previewChunkKeys.has(key)) this.setPreviewHidden(key, false);
    }
    for (const key of previewChunkKeys) this.setPreviewHidden(key, true);
    return true;
  }

  restoreAllSuppressedGroups(): void {
    for (const key of [...this.previewHidden]) this.setPreviewHidden(key, false);
  }

  /** A committed preview chunk's remesh has landed: give it back unless it is still pending. */
  restoreGroupIfComplete(chunkKey: string, pendingKeys: ReadonlySet<string>): void {
    if (!pendingKeys.has(chunkKey)) this.setPreviewHidden(chunkKey, false);
  }

  hasPendingSuppressions(): boolean { return false; }

  /** Nothing waits on a merge any more, so nothing needs meshing ahead of its turn. */
  getPriorityChunkKeys(): ReadonlySet<string> { return EMPTY_KEYS; }

  // ============== Seam strips ==============

  /**
   * A seam strip for (owner chunk, face, layer) — additive geometry bridging a cross-LOD border.
   * `null` retracts it. Strips carry absolute world positions (the stitcher builds them from both
   * sides' world vertices), so their instance matrix is the identity.
   */
  setApron(chunkKey: string, face: number, layer: number, apron: ApronMesh | null): void {
    const id = `${chunkKey}|${face}|${layer}`;
    const existing = this.aprons.get(id);
    if (!apron) {
      if (existing) { this.release(existing); this.aprons.delete(id); }
      return;
    }
    const owner = this.entries.get(chunkKey);
    const lod = owner?.lod ?? parseChunkKey(chunkKey).lod;

    // A throwaway BufferGeometry around the stitcher's arrays — never added to the scene, so it costs
    // no GPU buffer; the batch copies from it.
    const geo = new THREE.BufferGeometry();
    for (const a of TERRAIN_ATTRS) {
      const arr = a.name === 'position' ? apron.positions : apron.attrs[a.name];
      if (arr) geo.setAttribute(a.name, bufferAttribute(a, arr));
    }
    const count = geo.getAttribute('position').count;
    // Apron batches carry skirtKind for every strip (the F7 view's per-mechanism red); a strip
    // without one is plain (0).
    geo.setAttribute(SKIRT_KIND_ATTR.name, bufferAttribute(
      SKIRT_KIND_ATTR, apron.attrs.skirtKind ?? new Uint8Array(count)));
    geo.setIndex(new THREE.BufferAttribute(apron.indices, 1));
    // THE OWNER'S LEVEL, so the per-LOD texture scale matches the ring the strip is welded to.
    setLodLevelAttr(geo, lod);

    // BOUNDS: CULL WITH THE TERRAIN YOU BRIDGE, NOT WITH YOURSELF. A ribbon a few voxels wide is a far
    // tighter volume than the chunk cube its two sides are culled by, so at a screen edge the strip
    // left the frustum while both sides were still drawn — the border opened up. The floor is the
    // owner's world AABB grown by one coarse cell; the strip's real extent is unioned in.
    geo.computeBoundingBox();
    const box = geo.boundingBox!;
    if (owner) {
      const size = CHUNK_WORLD_SIZE * (1 << owner.lod);
      const sc = VOXEL_SCALE * (1 << (owner.lod + 1));
      box.expandByPoint(_v3.set(owner.wx - sc, owner.wy - sc, owner.wz - sc));
      box.expandByPoint(_v3.set(owner.wx + size + sc, owner.wy + size + sc, owner.wz + size + sc));
    }
    geo.boundingSphere = box.getBoundingSphere(new THREE.Sphere());

    const batch = this.batchFor(layer, 'apron');
    const placed = this.place(existing, batch, geo, null, true);
    if (placed) this.aprons.set(id, placed); else this.aprons.delete(id);
  }

  /** An owner-level skirt — one strip per (coarse owner, layer), keyed by the SKIRT_FACE sentinel. */
  setSkirt(ownerKey: string, layer: number, skirt: ApronMesh | null): void {
    this.setApron(ownerKey, SKIRT_FACE, layer, skirt);
  }

  // ============== Per-frame ==============

  /**
   * Room-making, on the frame clock — the only place a batch is rewritten. There is NO periodic
   * compaction: released ranges are recycled by place(), and dead space costs nothing until it would
   * force a growth (the buffer is allocated at capacity either way).
   */
  tick(budgetEnd: number): void {
    // ROOM FOR A BLOCKED APPLY FIRST, and regardless of the clock: an apply is waiting on it, and the
    // rewrite costs the same whichever frame it lands in — deferring it past a saturated frame would
    // only stall streaming. One per tick; the apply drain retries next frame.
    for (const b of this.batches.values()) {
      if (b.wantVerts === 0 && b.wantIdx === 0 && b.wantInst === 0) continue;
      if (b.wantInst && b.instances + 1 > b.instCap) { b.instCap *= 2; b.mesh.setInstanceCount(b.instCap); }
      this.ensureRoom(b, b.wantVerts, b.wantIdx);
      b.wantVerts = 0; b.wantIdx = 0; b.wantInst = 0;
      return;
    }
    if (performance.now() >= budgetEnd) return;
    // PRE-EMPTIVE, so the full-buffer rewrite lands on a frame of tick's choosing rather than on the
    // apply that tips the batch over. Substantial dead space is reclaimed (optimize) instead — same
    // cost class, but it buys the headroom without doubling the allocation.
    for (const b of this.batches.values()) {
      const vertsHigh = b.tailVerts * GROW_AT_DEN > b.vertCap * GROW_AT_NUM;
      const idxHigh = b.tailIdx * GROW_AT_DEN > b.idxCap * GROW_AT_NUM;
      if (!vertsHigh && !idxHigh) continue;
      if (b.tailVerts - b.liveVerts >= b.vertCap >> 3) this.compact(b);
      else this.grow(b, b.vertCap + 1, b.idxCap + 1);
      return;
    }
  }

  /**
   * The batch meshes drawing `layer`, every lod — for UI PICKING (the explore spawn marker raycasts
   * what is drawn). `BatchedMesh.raycast` tests per instance, so a hit is a real terrain hit; the
   * chunk's identity is not needed by the one caller.
   */
  pickables(layer: number): THREE.Object3D[] {
    const out: THREE.Object3D[] = [];
    for (const b of this.batches.values()) {
      if (b.kind === 'terrain' && b.layer === layer && b.instances > 0) out.push(b.mesh);
    }
    return out;
  }

  /** For the panel and the audit: batches on screen and how full they are, in total and each. */
  /** READ-ONLY, for headless probes: which chunks each batch mesh draws. A batch spans every lod
   *  (`lod` is −1); the per-chunk level is in each key. */
  debugMeshOwners(): Array<{
    mesh: THREE.Mesh; kind: 'group' | 'standalone' | 'apron'; lod: number; keys: string[];
  }> {
    const out: Array<{
      mesh: THREE.Mesh; kind: 'group' | 'standalone' | 'apron'; lod: number; keys: string[];
    }> = [];
    for (const b of this.batches.values()) {
      const keys: string[] = [];
      if (b.kind === 'terrain') {
        for (const e of this.entries.values()) {
          if (e.visible && !this.previewHidden.has(e.key) && e.layers[b.layer]) keys.push(e.key);
        }
      } else {
        for (const [id, p] of this.aprons) if (p.batch === b) keys.push(id.slice(0, id.indexOf('|')));
      }
      out.push({ mesh: b.mesh, kind: b.kind === 'apron' ? 'apron' : 'group', lod: -1, keys });
    }
    return out;
  }

  stats(): {
    batches: number; instances: number; vertCap: number; liveVerts: number; wasteVerts: number;
    roomWaits: number;
    perBatch: Array<{ id: string; instances: number; vertCap: number; liveVerts: number; wasteVerts: number }>;
  } {
    let instances = 0, vertCap = 0, liveVerts = 0, wasteVerts = 0;
    const perBatch = [];
    for (const [id, b] of this.batches) {
      const waste = b.tailVerts - b.liveVerts;
      instances += b.instances; vertCap += b.vertCap; liveVerts += b.liveVerts; wasteVerts += waste;
      perBatch.push({ id, instances: b.instances, vertCap: b.vertCap, liveVerts: b.liveVerts, wasteVerts: waste });
    }
    return { batches: this.batches.size, instances, vertCap, liveVerts, wasteVerts, roomWaits: this.roomWaits, perBatch };
  }

  dispose(): void {
    for (const b of this.batches.values()) {
      this.scene.remove(b.mesh);
      b.mesh.geometry.dispose();
      b.mesh.dispose();
    }
    this.batches.clear();
    this.entries.clear();
    this.visiblePacked.clear();
    this.aprons.clear();
    this.previewHidden.clear();
  }

  // ============== Internals ==============

  private setPreviewHidden(key: string, hidden: boolean): void {
    const was = this.previewHidden.has(key);
    if (was === hidden) return;
    if (hidden) this.previewHidden.add(key); else this.previewHidden.delete(key);
    const entry = this.entries.get(key);
    if (entry) this.applyVisibility(entry);
  }

  private applyVisibility(entry: ChunkEntry): void {
    const shown = entry.visible && !this.previewHidden.has(entry.key);
    for (let layer = 0; layer < LAYER_COUNT; layer++) {
      const p = entry.layers[layer];
      if (p) p.batch.mesh.setVisibleAt(p.instanceId, shown);
    }
  }

  /** Put a chunk's layer geometry in its batch (or take it out when the layer is empty). */
  private placeChunkLayer(entry: ChunkEntry, layer: number, geo: THREE.BufferGeometry | null): void {
    const existing = entry.layers[layer];
    if (!geo || !geo.index || geo.index.count === 0) {
      if (existing) { this.release(existing); entry.layers[layer] = null; }
      return;
    }
    // One attribute set per batch: terrain never carries skirtKind, and every geometry carries
    // lodLevel (the batch mixes levels). A re-meshed geometry arrives without the stamp.
    if (geo.hasAttribute(SKIRT_KIND_ATTR.name)) geo.deleteAttribute(SKIRT_KIND_ATTR.name);
    setLodLevelAttr(geo, entry.lod);
    const batch = this.batchFor(layer, 'terrain');
    const shown = entry.visible && !this.previewHidden.has(entry.key);
    entry.layers[layer] = this.place(existing, batch, geo, _m4.makeTranslation(entry.wx, entry.wy, entry.wz), shown);
  }

  /**
   * Copy `geo` into `batch`: in place when it still fits its reserved range in the same batch,
   * otherwise released and re-added at the tail. Returns the placement (null only for empty geometry).
   */
  private place(
    existing: Placement | null | undefined, batch: LayerBatch, geo: THREE.BufferGeometry,
    matrix: THREE.Matrix4 | null, visible: boolean,
  ): Placement | null {
    const verts = geo.getAttribute('position').count;
    const idx = geo.index!.count;
    if (existing && existing.batch === batch && verts <= existing.reservedVerts && idx <= existing.reservedIdx) {
      copyGeometryAt(batch.mesh, existing.geometryId, geo);
      existing.source = geo;
      if (matrix) batch.mesh.setMatrixAt(existing.instanceId, matrix);
      batch.mesh.setVisibleAt(existing.instanceId, visible);
      return existing;
    }
    if (existing) this.release(existing);

    const reservedVerts = reserve(verts, RESERVE_FLOOR_VERTS);
    const reservedIdx = reserve(idx, RESERVE_FLOOR_INDICES);
    if (batch.instances + 1 > batch.instCap) {
      batch.instCap *= 2;
      batch.mesh.setInstanceCount(batch.instCap);
    }

    // RECYCLE a released range when one fits — the geometryId stayed alive for exactly this, and the
    // copy is the same ranged setGeometryAt an in-place remesh pays. Taken over whole: the placement
    // keeps the range's own reservation (its slack is just bigger).
    const fr = this.takeFreeRange(batch, verts, idx, reservedVerts, reservedIdx);
    if (fr) {
      copyGeometryAt(batch.mesh, fr.geometryId, geo);
      const instanceId = batch.mesh.addInstance(fr.geometryId);
      batch.mesh.setMatrixAt(instanceId, matrix ?? _m4.identity());
      batch.mesh.setVisibleAt(instanceId, visible);
      batch.liveVerts += fr.reservedVerts; batch.liveIdx += fr.reservedIdx;
      batch.instances++;
      return { batch, geometryId: fr.geometryId, instanceId, reservedVerts: fr.reservedVerts, reservedIdx: fr.reservedIdx, source: geo };
    }

    // The safety net: the apply path asked hasRoomFor first, so for terrain this is a no-op; strips
    // (small batches) and the synchronous remesh path still make room here.
    this.ensureRoom(batch, reservedVerts, reservedIdx);
    const geometryId = batch.mesh.addGeometry(geo, reservedVerts, reservedIdx);
    const instanceId = batch.mesh.addInstance(geometryId);
    batch.mesh.setMatrixAt(instanceId, matrix ?? _m4.identity());
    batch.mesh.setVisibleAt(instanceId, visible);
    batch.tailVerts += reservedVerts; batch.tailIdx += reservedIdx;
    batch.liveVerts += reservedVerts; batch.liveIdx += reservedIdx;
    batch.instances++;
    return { batch, geometryId, instanceId, reservedVerts, reservedIdx, source: geo };
  }

  /** Best-fit free range for this geometry, or null. Oversized fits are refused while the tail is
   *  cheaper (REUSE_MAX_FACTOR); when the tail is full, any fit beats a rewrite. */
  private takeFreeRange(b: LayerBatch, verts: number, idx: number, rv: number, ri: number): FreeRange | null {
    let best = -1;
    for (let i = 0; i < b.free.length; i++) {
      const fr = b.free[i];
      if (fr.reservedVerts < verts || fr.reservedIdx < idx) continue;
      if (best === -1 || fr.reservedVerts < b.free[best].reservedVerts) best = i;
    }
    if (best === -1) return null;
    const fr = b.free[best];
    const tailFits = b.tailVerts + rv <= b.vertCap && b.tailIdx + ri <= b.idxCap;
    if (tailFits && fr.reservedVerts > rv * REUSE_MAX_FACTOR) return null;
    b.free[best] = b.free[b.free.length - 1];
    b.free.pop();
    return fr;
  }

  /** The range goes to the free list, not to deleteGeometry — a later chunk that fits takes it over. */
  private release(p: Placement): void {
    p.batch.mesh.deleteInstance(p.instanceId);
    p.batch.free.push({ geometryId: p.geometryId, reservedVerts: p.reservedVerts, reservedIdx: p.reservedIdx });
    p.batch.liveVerts -= p.reservedVerts;
    p.batch.liveIdx -= p.reservedIdx;
    p.batch.instances--;
  }

  /** Make room at the tail for a new range: compact if the dead space covers it, else grow. */
  private ensureRoom(b: LayerBatch, verts: number, idx: number): void {
    if (b.instances + 1 > b.instCap) {
      b.instCap *= 2;
      b.mesh.setInstanceCount(b.instCap);
    }
    if (verts === 0 && idx === 0) return;
    if (b.tailVerts + verts <= b.vertCap && b.tailIdx + idx <= b.idxCap) return;
    if (b.liveVerts + verts <= b.vertCap && b.liveIdx + idx <= b.idxCap) {
      this.compact(b);
      return;
    }
    this.grow(b, b.liveVerts + verts, b.liveIdx + idx);
  }

  /** Double the capacity until (needVerts, needIdx) fit the LIVE content — a full re-upload, logged. */
  private grow(b: LayerBatch, needVerts: number, needIdx: number): void {
    let vertCap = b.vertCap, idxCap = b.idxCap;
    while (needVerts > vertCap) vertCap *= 2;
    while (needIdx > idxCap) idxCap *= 2;
    // Compact first so the grown buffer is packed, then grow: setGeometrySize re-uploads everything.
    if (b.tailVerts !== b.liveVerts || b.tailIdx !== b.liveIdx) this.compact(b);
    b.mesh.setGeometrySize(vertCap, idxCap);
    b.vertCap = vertCap; b.idxCap = idxCap;
    noteHighWater(`${b.kind}|${b.layer}`, vertCap, idxCap);
    this.growths++;
    console.info(`[TerrainBatch] grew layer${b.layer} ${b.kind} to ${vertCap} verts / ${idxCap} indices`);
  }

  /**
   * Reclaim dead space: the free ranges are dropped for real (deleteGeometry) and the survivors are
   * repacked (`optimize()` — a full buffer rewrite). Reached only when it avoids or accompanies a
   * growth; recycling makes the periodic version unnecessary.
   */
  private compact(b: LayerBatch): void {
    for (const fr of b.free) b.mesh.deleteGeometry(fr.geometryId);
    b.free.length = 0;
    b.mesh.optimize();
    b.tailVerts = b.liveVerts;
    b.tailIdx = b.liveIdx;
    this.compactions++;
  }

  private batchFor(layer: number, kind: Kind): LayerBatch {
    const id = `${kind}|${layer}`;
    let b = this.batches.get(id);
    if (b) return b;
    loadHighWater();
    const hw = highWater.get(id);
    // The larger of the size model and this machine's remembered demand — the growth ladder for a
    // biome is paid once ever, not once per world/mode switch (each recreates the batches).
    const verts = Math.max(initialVerts(layer, kind, this.viewScale), hw?.verts ?? 0);
    const idx = Math.max(verts * INDICES_PER_VERT, hw?.idx ?? 0);
    const mesh = new THREE.BatchedMesh(INITIAL_INSTANCES, verts, idx, layerMaterial(layer));
    // Material, shadows, renderOrder, custom depth — the layer's, as for any terrain mesh. The F7
    // view colours terrain by the vertex `lodLevel`; a strip batch is tagged 'skirt' and casts like
    // the layer it bridges.
    configureLayerMesh(mesh, layer, kind === 'apron' ? 'skirt' : 0);
    if (layer === LAYER_LIQUID) mesh.castShadow = false;
    // Culling happens per geometry inside the batch (against the camera, and against the shadow
    // camera on the shadow pass — which is what replaces the grouper's shadow-radius culling).
    // The batch itself is never culled as a whole: its own bounding sphere is not kept current.
    mesh.perObjectFrustumCulled = true;
    mesh.frustumCulled = false;
    mesh.matrixAutoUpdate = false;
    mesh.updateMatrixWorld(true);
    mesh.name = id;
    mesh.visible = this.drawGate;
    this.scene.add(mesh);
    b = {
      mesh, layer, kind,
      vertCap: verts, idxCap: idx, instCap: INITIAL_INSTANCES,
      tailVerts: 0, tailIdx: 0, liveVerts: 0, liveIdx: 0, instances: 0,
      free: [],
      wantVerts: 0, wantIdx: 0, wantInst: 0,
    };
    this.batches.set(id, b);
    return b;
  }
}
